import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Trash2, Download, Search, ShieldCheck, Users, ClipboardList, Brain,
  Plus, ExternalLink, Pencil, FileText, RefreshCw, Database,
} from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const PRESET_COLORS = ["#0d9488", "#0891b2", "#1d4ed8", "#7c3aed", "#db2777", "#dc2626", "#65a30d", "#ca8a04"];

function StatCard({ icon: Icon, value, label, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={testid}>
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><Icon className="h-5 w-5" /></span>
      <div className="font-display mt-4 text-3xl font-semibold text-primary">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function BarRow({ name, count, max, color = "bg-primary" }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-40 shrink-0 truncate font-medium" title={name}>{name}</span>
      <div className="h-3 flex-1 overflow-hidden rounded-full bg-secondary">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${(count / max) * 100}%` }} />
      </div>
      <span className="w-8 text-right text-xs text-muted-foreground">{count}</span>
    </div>
  );
}

export default function Admin() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [qSearch, setQSearch] = useState("");
  const [control, setControl] = useState(null);
  const [faqForm, setFaqForm] = useState({ q: "", a: "" });
  const [testiForm, setTestiForm] = useState({ quote: "", name: "", role: "" });
  const [pageForm, setPageForm] = useState({ id: null, title: "", slug: "", content: "", published: true });
  const [filters, setFilters] = useState({ q: "", grade: "all", archetype: "all", from: "", to: "" });
  const [design, setDesign] = useState({ primaryColor: "#0d9488", accentRadius: "0.75rem", showHeroImage: true });

  const loadAll = useCallback(() => {
    api.get("/api/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/api/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/api/admin/results").then((r) => setReports(r.data)).catch(() => {});
    api.get("/api/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/api/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/api/admin/pages").then((r) => setPages(r.data)).catch(() => {});
    api.get("/api/questions", { params: { lang } }).then((r) => setQuestions(r.data)).catch(() => {});
    api.get("/api/admin/control/status").then((r) => setControl(r.data)).catch(() => {});
    api.get("/api/settings").then((r) => {
      setSettings(r.data);
      setDesign({
        primaryColor: r.data.primaryColor || "#0d9488",
        accentRadius: r.data.accentRadius || "0.75rem",
        showHeroImage: r.data.showHeroImage !== false,
      });
    }).catch(() => {});
  }, [lang]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async (payload) => {
    try {
      await api.put("/api/admin/settings", payload);
      toast.success(t("admin.save") + " ✓");
      loadAll();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const addFaq = async (e) => {
    e.preventDefault();
    try { await api.post("/api/admin/faqs", { ...faqForm, order: faqs.length }); setFaqForm({ q: "", a: "" }); loadAll(); toast.success("Added"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const addTesti = async (e) => {
    e.preventDefault();
    try { await api.post("/api/admin/testimonials", { ...testiForm, order: testimonials.length }); setTestiForm({ quote: "", name: "", role: "" }); loadAll(); toast.success("Added"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const savePage = async (e) => {
    e.preventDefault();
    try {
      if (pageForm.id) await api.put(`/api/admin/pages/${pageForm.id}`, pageForm);
      else await api.post("/api/admin/pages", pageForm);
      setPageForm({ id: null, title: "", slug: "", content: "", published: true });
      loadAll(); toast.success(t("admin.save") + " ✓");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const applyDesign = async (e) => {
    e.preventDefault();
    await saveSettings(design);
  };
  const toggleFlag = async (key, value) => {
    await saveSettings({ [key]: value });
  };

  const gradeOptions = Array.from(new Set(reports.map((r) => r.grade).filter(Boolean))).sort();
  const archetypeOptions = Array.from(new Set(reports.map((r) => r.archetypeName).filter(Boolean))).sort();
  const filteredReports = reports.filter((r) => {
    if (filters.grade !== "all" && r.grade !== filters.grade) return false;
    if (filters.archetype !== "all" && r.archetypeName !== filters.archetype) return false;
    if (filters.q) {
      const hay = `${r.studentName || ""} ${r.userEmail || ""}`.toLowerCase();
      if (!hay.includes(filters.q.toLowerCase())) return false;
    }
    const day = (r.createdAt || "").slice(0, 10);
    if (filters.from && day < filters.from) return false;
    if (filters.to && day > filters.to) return false;
    return true;
  });

  const exportCsv = async () => {
    try {
      const params = {};
      if (filters.grade !== "all") params.grade = filters.grade;
      if (filters.archetype !== "all") params.archetype = filters.archetype;
      if (filters.q) params.q = filters.q;
      if (filters.from) params.date_from = filters.from;
      if (filters.to) params.date_to = filters.to;
      const res = await api.get("/api/admin/results/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `edvia-results-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const archMax = stats ? Math.max(1, ...stats.archetypeDistribution.map((d) => d.count)) : 1;
  const signupMax = stats ? Math.max(1, ...stats.dailySignups.map((d) => d.count)) : 1;
  const filteredQuestions = questions.filter((q) => {
    const s = qSearch.toLowerCase();
    return q.text.toLowerCase().includes(s) || q.id.toLowerCase().includes(s)
      || (q.signalLabel || "").toLowerCase().includes(s) || (q.signal || "").toLowerCase().includes(s)
      || String(q.order) === s.trim();
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-12" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          <ShieldCheck className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="admin-title">{t("admin.console")}</h1>
          <p className="text-sm text-muted-foreground">{t("admin.consoleSub")}</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="flex h-auto flex-wrap justify-start" data-testid="admin-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">{t("admin.overview")}</TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions">{t("admin.questions")}</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">{t("admin.results")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("admin.users")}</TabsTrigger>
          <TabsTrigger value="pages" data-testid="tab-pages">{t("admin.pages")}</TabsTrigger>
          <TabsTrigger value="site" data-testid="tab-site">{t("admin.site")}</TabsTrigger>
          <TabsTrigger value="design" data-testid="tab-design">{t("admin.design")}</TabsTrigger>
          <TabsTrigger value="control" data-testid="tab-control">{t("admin.control")}</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          {stats && (
            <div className="mt-6 space-y-8">
              <div className="grid gap-4 sm:grid-cols-3">
                <StatCard icon={Users} value={stats.totalUsers} label={t("admin.totalUsers")} testid="stat-users" />
                <StatCard icon={ClipboardList} value={stats.totalResults} label={t("admin.testsTaken")} testid="stat-tests" />
                <StatCard icon={Brain} value={stats.totalQuestions} label={t("admin.questionsStat")} testid="stat-questions" />
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl font-semibold">{t("admin.dist")}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{t("admin.distSub")}</p>
                <div className="mt-4 space-y-2" data-testid="archetype-distribution">
                  {stats.archetypeDistribution.length === 0 && <p className="text-sm text-muted-foreground">{t("admin.noData")}</p>}
                  {stats.archetypeDistribution.map((d) => <BarRow key={d.name} name={d.name} count={d.count} max={archMax} />)}
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="daily-signups">
                <h3 className="font-display text-xl font-semibold">{t("admin.signups")}</h3>
                <div className="mt-6 flex h-44 items-stretch gap-1 sm:gap-2">
                  {stats.dailySignups.map((d) => {
                    const [y, m, day] = d.date.split("-");
                    const pct = (d.count / signupMax) * 100;
                    return (
                      <div key={d.date} className="flex flex-1 flex-col items-center justify-end gap-1.5" data-testid={`signup-bar-${d.date}`}>
                        <span className={`text-xs font-semibold ${d.count ? "text-foreground" : "text-transparent"}`}>{d.count}</span>
                        <div
                          className="w-full max-w-[46px] rounded-t-md transition-all"
                          style={{
                            height: `${Math.max(2, pct)}%`,
                            background: "linear-gradient(180deg, #38bdf8 0%, #14b8a6 100%)",
                            opacity: d.count ? 1 : 0.15,
                          }}
                          title={`${day}/${m}: ${d.count}`}
                        />
                        <span className="text-[9px] text-muted-foreground">{day}/{m}</span>
                      </div>
                    );
                  })}
                </div>
                {stats.dailySignups.every((d) => d.count === 0) && <p className="mt-2 text-sm text-muted-foreground">{t("admin.noSignups")}</p>}
              </div>
              <div className="grid gap-8 md:grid-cols-2">
                <div className="rounded-2xl border border-border bg-card p-6" data-testid="grade-distribution">
                  <h3 className="font-display text-xl font-semibold">{t("admin.byGrade")}</h3>
                  <div className="mt-4 space-y-2">
                    {(stats.gradeDistribution || []).map((d) => <BarRow key={d.name} name={d.name} count={d.count} max={Math.max(1, ...stats.gradeDistribution.map((x) => x.count))} color="bg-cyan-600" />)}
                  </div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-6" data-testid="school-distribution">
                  <h3 className="font-display text-xl font-semibold">{t("admin.bySchool")}</h3>
                  <div className="mt-4 space-y-2">
                    {(stats.schoolDistribution || []).length === 0 && <p className="text-sm text-muted-foreground">{t("admin.noSchools")}</p>}
                    {(stats.schoolDistribution || []).map((d) => <BarRow key={d.name} name={d.name} count={d.count} max={Math.max(1, ...stats.schoolDistribution.map((x) => x.count))} color="bg-amber-500" />)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* QUESTIONS */}
        <TabsContent value="questions">
          <div className="mt-6">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder={t("admin.qSearch")} value={qSearch} onChange={(e) => setQSearch(e.target.value)} data-testid="questions-search" />
            </div>
            <p className="mt-3 text-xs text-muted-foreground" data-testid="questions-count">{filteredQuestions.length} / {questions.length}</p>
            <div className="mt-3 space-y-2" data-testid="questions-list">
              {filteredQuestions.map((q) => (
                <div key={q.id} className="rounded-xl border border-border bg-card p-4 text-sm" data-testid={`q-${q.id}`}>
                  <span className="mr-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-primary">{q.id}</span>
                  {q.signalLabel && <span className="mr-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">{q.signalLabel}</span>}
                  {q.text}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* RESULTS */}
        <TabsContent value="results">
          <div className="mt-6 flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-card p-4" data-testid="reports-filters">
            <div className="relative min-w-[180px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search name or email" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filter-search" />
            </div>
            <Select value={filters.grade} onValueChange={(v) => setFilters({ ...filters, grade: v })}>
              <SelectTrigger className="w-[150px]" data-testid="filter-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All grades</SelectItem>{gradeOptions.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.archetype} onValueChange={(v) => setFilters({ ...filters, archetype: v })}>
              <SelectTrigger className="w-[180px]" data-testid="filter-archetype"><SelectValue placeholder="Archetype" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All archetypes</SelectItem>{archetypeOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex flex-col"><label className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">From</label><Input type="date" className="w-[150px]" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="filter-from" /></div>
            <div className="flex flex-col"><label className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">To</label><Input type="date" className="w-[150px]" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="filter-to" /></div>
            <Button variant="ghost" size="sm" onClick={() => setFilters({ q: "", grade: "all", archetype: "all", from: "", to: "" })} data-testid="filter-clear">Clear</Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-button"><Download className="mr-1 h-4 w-4" />Export CSV</Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground" data-testid="reports-count">{filteredReports.length} of {reports.length} results</p>
          <div className="mt-2 overflow-x-auto rounded-2xl border border-border bg-card" data-testid="reports-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground"><th className="p-4">Student</th><th className="p-4">Type</th><th className="p-4">Grade</th><th className="p-4">Date</th><th className="p-4"></th></tr></thead>
              <tbody>
                {filteredReports.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0" data-testid={`report-row-${r.id}`}>
                    <td className="p-4"><div className="font-medium">{r.studentName}</div><div className="text-xs text-muted-foreground">{r.userEmail}</div></td>
                    <td className="p-4">{r.archetypeName || r.type}</td>
                    <td className="p-4 text-muted-foreground">{r.grade || "—"}</td>
                    <td className="p-4 text-muted-foreground">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}</td>
                    <td className="p-4"><Link to={`/results/${r.id}`} className="font-medium text-primary hover:underline" data-testid={`view-report-${r.id}`}>{t("dash.view")} →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card" data-testid="students-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground"><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Grade</th><th className="p-4">{t("admin.reports")}</th><th className="p-4">Joined</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0" data-testid={`student-row-${u.id}`}>
                    <td className="p-4 font-medium">{u.name}{u.role === "admin" && <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-primary">admin</span>}</td>
                    <td className="p-4 text-muted-foreground">{u.email}</td>
                    <td className="p-4 text-muted-foreground">{u.grade || "—"}</td>
                    <td className="p-4">{u.resultsCount}</td>
                    <td className="p-4 text-muted-foreground">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* PAGES */}
        <TabsContent value="pages">
          <div className="mt-6 grid gap-8 lg:grid-cols-2">
            <form onSubmit={savePage} className="space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="page-form">
              <h3 className="font-display text-lg font-semibold">{pageForm.id ? t("admin.edit") : t("admin.newPage")}</h3>
              <Input placeholder={t("admin.pageTitle")} value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} required data-testid="page-title-input" />
              <Input placeholder={t("admin.pageSlug")} value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value })} required disabled={!!pageForm.id} data-testid="page-slug-input" />
              <Textarea rows={5} placeholder={t("admin.pageContent")} value={pageForm.content} onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })} required data-testid="page-content-input" />
              <label className="flex items-center gap-2 text-sm"><Switch checked={pageForm.published} onCheckedChange={(v) => setPageForm({ ...pageForm, published: v })} data-testid="page-published-toggle" />{t("admin.published")}</label>
              <div className="flex gap-2">
                <Button type="submit" size="sm" data-testid="page-save-button">{t("admin.save")}</Button>
                {pageForm.id && <Button type="button" size="sm" variant="ghost" onClick={() => setPageForm({ id: null, title: "", slug: "", content: "", published: true })}>Cancel</Button>}
              </div>
            </form>
            <ul className="space-y-2" data-testid="pages-list">
              {pages.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 text-sm" data-testid={`page-row-${p.id}`}>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <div><div className="font-semibold">{p.title}</div><div className="text-xs text-muted-foreground">/p/{p.slug}{!p.published && " · draft"}</div></div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/p/${p.slug}`} target="_blank" className="p-1.5 text-muted-foreground hover:text-primary" data-testid={`page-view-${p.id}`}><ExternalLink className="h-4 w-4" /></Link>
                    <button onClick={() => setPageForm({ id: p.id, title: p.title, slug: p.slug, content: p.content, published: p.published })} className="p-1.5 text-muted-foreground hover:text-primary" data-testid={`page-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={async () => { await api.delete(`/api/admin/pages/${p.id}`); loadAll(); }} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`page-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </li>
              ))}
              {pages.length === 0 && <p className="text-sm text-muted-foreground">{t("admin.noPages")}</p>}
            </ul>
          </div>
        </TabsContent>

        {/* SITE */}
        <TabsContent value="site">
          <div className="mt-6 grid gap-8 lg:grid-cols-2">
            <div className="space-y-8">
              {settings && (
                <form onSubmit={(e) => { e.preventDefault(); saveSettings({ siteName: settings.siteName, heroTitle: settings.heroTitle, heroSubtitle: settings.heroSubtitle, footerTagline: settings.footerTagline || "" }); }} className="space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="settings-form">
                  <h3 className="font-display text-lg font-semibold">{t("admin.siteContent")}</h3>
                  <Input placeholder={t("admin.siteName")} value={settings.siteName || ""} onChange={(e) => setSettings({ ...settings, siteName: e.target.value })} data-testid="settings-site-name" />
                  <Input placeholder={t("admin.heroTitle")} value={settings.heroTitle || ""} onChange={(e) => setSettings({ ...settings, heroTitle: e.target.value })} data-testid="settings-hero-title" />
                  <Textarea placeholder={t("admin.heroSubtitle")} value={settings.heroSubtitle || ""} onChange={(e) => setSettings({ ...settings, heroSubtitle: e.target.value })} data-testid="settings-hero-subtitle" />
                  <Input placeholder={t("admin.footerTag")} value={settings.footerTagline || ""} onChange={(e) => setSettings({ ...settings, footerTagline: e.target.value })} data-testid="settings-footer-tag" />
                  <Button type="submit" size="sm" data-testid="settings-save-button">{t("admin.save")}</Button>
                </form>
              )}
              <div>
                <h3 className="font-display text-lg font-semibold">{t("admin.testimonials")}</h3>
                <form onSubmit={addTesti} className="mt-3 space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="testimonial-form">
                  <Textarea placeholder={t("admin.quote")} value={testiForm.quote} onChange={(e) => setTestiForm({ ...testiForm, quote: e.target.value })} required data-testid="testimonial-quote-input" />
                  <div className="flex gap-3"><Input placeholder={t("auth.name")} value={testiForm.name} onChange={(e) => setTestiForm({ ...testiForm, name: e.target.value })} required data-testid="testimonial-name-input" /><Input placeholder={t("admin.role")} value={testiForm.role} onChange={(e) => setTestiForm({ ...testiForm, role: e.target.value })} data-testid="testimonial-role-input" /></div>
                  <Button type="submit" size="sm" data-testid="testimonial-add-button">{t("admin.add")}</Button>
                </form>
                <ul className="mt-4 space-y-2" data-testid="admin-testimonial-list">
                  {testimonials.map((tm) => (
                    <li key={tm.id} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4 text-sm">
                      <div><div>{tm.quote}</div><div className="mt-1 text-xs text-muted-foreground">{tm.name} · {tm.role}</div></div>
                      <button onClick={async () => { await api.delete(`/api/admin/testimonials/${tm.id}`); loadAll(); }} className="text-muted-foreground hover:text-destructive" data-testid={`testimonial-delete-${tm.id}`}><Trash2 className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">{t("admin.faqs")}</h3>
              <form onSubmit={addFaq} className="mt-3 space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="faq-form">
                <Input placeholder={t("admin.question")} value={faqForm.q} onChange={(e) => setFaqForm({ ...faqForm, q: e.target.value })} required data-testid="faq-q-input" />
                <Textarea placeholder={t("admin.answer")} value={faqForm.a} onChange={(e) => setFaqForm({ ...faqForm, a: e.target.value })} required data-testid="faq-a-input" />
                <Button type="submit" size="sm" data-testid="faq-add-button">{t("admin.add")}</Button>
              </form>
              <ul className="mt-4 space-y-2" data-testid="admin-faq-list">
                {faqs.map((f) => (
                  <li key={f.id} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4 text-sm">
                    <div><div className="font-semibold">{f.q}</div><div className="mt-1 text-xs text-muted-foreground">{f.a}</div></div>
                    <button onClick={async () => { await api.delete(`/api/admin/faqs/${f.id}`); loadAll(); }} className="text-muted-foreground hover:text-destructive" data-testid={`faq-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* DESIGN */}
        <TabsContent value="design">
          <form onSubmit={applyDesign} className="mt-6 max-w-xl space-y-6 rounded-2xl border border-border bg-card p-6" data-testid="design-form">
            <div>
              <h3 className="font-display text-lg font-semibold">{t("admin.primaryColor")}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setDesign({ ...design, primaryColor: c })}
                    className={`h-9 w-9 rounded-full border-2 transition-transform ${design.primaryColor === c ? "scale-110 border-foreground" : "border-transparent"}`}
                    style={{ backgroundColor: c }} data-testid={`color-${c}`} aria-label={c} />
                ))}
                <Input className="w-28" value={design.primaryColor} onChange={(e) => setDesign({ ...design, primaryColor: e.target.value })} data-testid="color-custom" />
              </div>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">{t("admin.radius")}</h3>
              <div className="mt-3 flex gap-2">
                {["0.25rem", "0.5rem", "0.75rem", "1rem", "1.5rem"].map((r) => (
                  <button key={r} type="button" onClick={() => setDesign({ ...design, accentRadius: r })}
                    className={`rounded-lg border px-3 py-2 text-xs ${design.accentRadius === r ? "border-primary bg-secondary font-semibold" : "border-border"}`}
                    data-testid={`radius-${r}`}>{r}</button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={design.showHeroImage} onCheckedChange={(v) => setDesign({ ...design, showHeroImage: v })} data-testid="hero-image-toggle" />
              {t("admin.showHero")}
            </label>
            <Button type="submit" data-testid="design-save-button">{t("admin.applyDesign")}</Button>
            <p className="text-xs text-muted-foreground">{t("admin.designNote")}</p>
          </form>
        </TabsContent>

        {/* CONTROL PANEL */}
        <TabsContent value="control">
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="space-y-3 rounded-2xl border border-border bg-card p-6" data-testid="control-toggles">
              <h3 className="font-display text-lg font-semibold">{t("admin.control")}</h3>
              {settings && (
                <>
                  <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-sm">
                    <span>{t("admin.signupToggle")}</span>
                    <Switch checked={settings.signupEnabled !== false} onCheckedChange={(v) => toggleFlag("signupEnabled", v)} data-testid="toggle-signup" />
                  </label>
                  <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-sm">
                    <span>{t("admin.assessToggle")}</span>
                    <Switch checked={settings.assessmentEnabled !== false} onCheckedChange={(v) => toggleFlag("assessmentEnabled", v)} data-testid="toggle-assessment" />
                  </label>
                  <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-sm">
                    <span>{t("admin.maintToggle")}</span>
                    <Switch checked={settings.maintenanceMode === true} onCheckedChange={(v) => toggleFlag("maintenanceMode", v)} data-testid="toggle-maintenance" />
                  </label>
                </>
              )}
            </div>
            <div className="space-y-3 rounded-2xl border border-border bg-card p-6" data-testid="control-actions">
              <h3 className="font-display text-lg font-semibold">{t("admin.maintenance")}</h3>
              {control && (
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>{t("admin.expired")}: <span className="font-semibold text-foreground">{control.expiredOtps}</span></div>
                  <div>{t("admin.lastBackup")}: <span className="font-semibold text-foreground">{control.lastBackup ? new Date(control.lastBackup.createdAt).toLocaleString() : "—"}</span></div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={async () => { const { data } = await api.post("/api/admin/control/prune-tokens"); toast.success(`Removed ${data.otpRemoved} codes, ${data.resetsRemoved} links`); loadAll(); }} data-testid="prune-tokens-button">
                  <RefreshCw className="mr-1 h-4 w-4" />{t("admin.prune")}
                </Button>
                <Button variant="outline" size="sm" onClick={async () => { await api.post("/api/admin/control/backup-log"); toast.success("Backup logged"); loadAll(); }} data-testid="backup-log-button">
                  <Database className="mr-1 h-4 w-4" />{t("admin.backup")}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
